import type { BotLine } from '../types/chat';
import type { QuestEvent, QuestStep } from '../utils/quest';

/**
 * Everything Pixel says during the hunt, from the chat corner. He never leaves it until all five
 * shards are found — he just gives clues.
 */

/** Phones only, right after "Quest mode: ON.": he can be tapped to tuck his chat away */
export const questStartTip: BotLine[] = [
  { text: 'Tip: tap me to hide our chat. Tap me again to bring it back.', emotion: 'happy' },
];

/** What Pixel says the moment something happens in the quest */
export const questEventLines: Partial<Record<QuestEvent, BotLine[]>> = {
  start: [
    { text: 'Quest mode: ON.', emotion: 'happy' },
    { text: 'I’ll stay right here and feed you clues. You do the finding.', emotion: 'curious' },
    { text: 'Shard one slipped behind something everyone is told to click. Way up top.', emotion: 'curious' },
  ],
  'found-button': [
    { text: 'THAT’S IT! Shard one!', emotion: 'surprised' },
    { text: 'Memory unlocked: my first word was “render”.', emotion: 'happy' },
    { text: 'Shard two is somewhere … worldly. Among the things we make.', emotion: 'curious' },
  ],
  'found-service': [
    { text: 'Shard two! You’re suspiciously good at this.', emotion: 'happy' },
    { text: 'Shard three ran back to where all of this began. You walked right past it.', emotion: 'curious' },
    { text: 'Think: what’s the very first thing anyone sees on this site?', emotion: 'curious' },
  ],
  'found-logo': [
    { text: 'Shard three! Hiding behind our own name. Cheeky.', emotion: 'surprised' },
    { text: 'Memory unlocked: Glitchara has a third moon. Shift. Nobody talks about Shift.', emotion: 'happy' },
    { text: 'Shard four is tucked behind one of our projects. The one that sparkles most.', emotion: 'curious' },
  ],
  'found-project': [
    { text: 'Shard four! Memory unlocked: I wasn’t in that red car by accident.', emotion: 'happy' },
    { text: 'The last one fell all the way down. Behind a big question.', emotion: 'curious' },
  ],
  // found-footer and the whole restoration are told by QuestFinale
  quit: [
    { text: 'Okay. Quest paused. Memory: still rattly.', emotion: 'sad' },
    { text: 'Thanks for trying. You were great. Mostly.', emotion: 'happy' },
  ],
};

/** Escalating hints per step (“Hint, please”, or by themselves when you're stuck) */
export const questHints: Record<QuestStep, BotLine[]> = {
  button: [
    { text: 'Things hide BEHIND buttons. Grab “Contact us” and drag it away. Today it’s allowed.', emotion: 'happy' },
    { text: 'Hold “Contact us”, pull it aside, then click the shard underneath.', emotion: 'curious' },
    { text: 'It’s the black “Contact us” button at the very top. Drag it. Really.', emotion: 'curious' },
  ],
  service: [
    { text: 'One of our services sounds like a place you could live in. Open it.', emotion: 'curious' },
    { text: 'Look through the services list. One title is a whole world.', emotion: 'curious' },
    { text: 'Open “Virtual Worlds”. The shard is napping inside.', emotion: 'happy' },
  ],
  logo: [
    { text: 'Every website starts with a name. Ours too. Go back to the very beginning.', emotion: 'curious' },
    { text: 'Scroll all the way up. Top-left corner. Who’s sitting there?', emotion: 'curious' },
    { text: 'Drag our logo in the top-left corner of the first screen aside. The shard is right behind it.', emotion: 'happy' },
  ],
  project: [
    { text: 'Something sparkly in our work is sitting on it.', emotion: 'curious' },
    { text: 'Jewellery! One of the project pictures. Drag the picture aside.', emotion: 'curious' },
    { text: 'Drag the Unique Jewelry picture out of the way.', emotion: 'happy' },
  ],
  footer: [
    { text: 'The big question in the footer looks heavy. Move it.', emotion: 'curious' },
    { text: 'Drag “Ready to be explored?” away. The shard is under it.', emotion: 'happy' },
    { text: 'Grab the big headline in the footer and pull. The shard is right underneath.', emotion: 'happy' },
  ],
  restore: [],
  finale: [],
};
